/**
 * Process-Centric View Component
 * 
 * Main view for browsing and managing processes.
 * Shows processes organized by process_type with detailed information.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Table,
  Tag,
  Button,
  Space,
  Input,
  Select,
  Tooltip,
  Badge,
  message,
  Spin,
  Collapse
} from 'antd';
import {
  SearchOutlined,
  FilterOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { apiClient } from '../../services/apiClient';
import { ProcessType } from '../../types/enums';
import type { ColumnsType } from 'antd/es/table';
import styles from './ProcessCentricView.module.css';

const { Search } = Input;
const { Option } = Select;

interface ProcessInfo {
  id: string;
  name: string;
  process_type: string;
  description?: string;
  artifact_count?: number;
  transition_count?: number;
  is_complete?: boolean;
  owner_artifact_name?: string;
  owner_artifact_id?: string;
}

interface ProcessArtifact {
  artifact_id: string;
  artifact_name: string;
  role: string;
}

interface ProcessWithArtifacts extends ProcessInfo {
  artifacts: ProcessArtifact[];
}

interface ProcessCentricViewProps {
  selectedArtifactId?: string;
  artifactTreeId?: string;
  companyId?: string;
  /** Prime Artifacts UI must use the prime process plane (not company /artifacts/.../processes). */
  modelType?: 'prime' | 'company';
  onProcessClick?: (processId: string) => void;
}

const getRoleColor = (role: string): string => {
  const colors: Record<string, string> = {
    'PROCESSITEM': 'blue',
    'PROCESSITEM_PERSISTENT': 'cyan',
    'MATERIAL': 'green',
    'RESOURCE': 'orange',
    'CREATION': 'purple'
  };
  return colors[role] || 'default';
};

const getProcessTypeColor = (type: string): string => {
  const colors: Record<string, string> = {
    [ProcessType.DEFAULT]: 'default',
    [ProcessType.DEMAND]: 'magenta',
    [ProcessType.SUPPLY]: 'blue',
    [ProcessType.DELIVERY]: 'green',
    [ProcessType.FINANCE]: 'gold',
    [ProcessType.HR]: 'purple',
    [ProcessType.IT]: 'cyan',
    [ProcessType.STRATEGY]: 'orange',
    [ProcessType.IMPROVEMENT]: 'lime',
    [ProcessType.QUALITY]: 'green',
    [ProcessType.COMPLIANCE]: 'red',
    [ProcessType.RESEARCH]: 'blue',
    [ProcessType.MARKETFIT]: 'magenta',
    [ProcessType.FITFINDER]: 'purple'
  };
  return colors[type] || 'default';
};

export const ProcessCentricView: React.FC<ProcessCentricViewProps> = ({
  selectedArtifactId,
  artifactTreeId,
  companyId,
  modelType,
  onProcessClick
}) => {
  const [loading, setLoading] = useState(true);
  const [processes, setProcesses] = useState<ProcessWithArtifacts[]>([]);
  const [filteredProcesses, setFilteredProcesses] = useState<ProcessWithArtifacts[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [completionFilter, setCompletionFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'list' | 'grouped'>('grouped');
  /** Progressive prime overview fetch (100-row pages until exhausted). */
  const [primeFetchMeta, setPrimeFetchMeta] = useState<{
    loaded: number;
    pageSize: number;
    complete: boolean;
    typeFilter: string;
  } | null>(null);
  const primeFetchGenRef = useRef(0);

  const isPrimeContext =
    modelType === 'prime' || (!artifactTreeId && !companyId && modelType !== 'company');

  useEffect(() => {
    void loadProcesses();
    return () => {
      // Invalidate in-flight progressive prime fetches when deps change/unmount.
      primeFetchGenRef.current += 1;
    };
  }, [selectedArtifactId, artifactTreeId, companyId, modelType, typeFilter]);

  useEffect(() => {
    filterProcesses();
  }, [processes, searchTerm, completionFilter]);

  const normalizeProcessList = (data: unknown): ProcessInfo[] => {
    if (Array.isArray(data)) return data as ProcessInfo[];
    if (data && typeof data === 'object') {
      const obj = data as Record<string, unknown>;
      if (Array.isArray(obj.processes)) return obj.processes as ProcessInfo[];
      if (Array.isArray(obj.items)) return obj.items as ProcessInfo[];
    }
    return [];
  };

  const mapPrimeProcessRow = (p: any, owner?: { name: string; id: string }): ProcessInfo => ({
    ...p,
    process_type: p.process_type || p.process_type_name,
    is_complete: p.is_complete ?? p.is_active,
    ...(owner
      ? { owner_artifact_name: owner.name, owner_artifact_id: owner.id }
      : {}),
  });

  const toOverviewRow = (process: ProcessInfo): ProcessWithArtifacts => ({
    ...process,
    artifacts: [],
    artifact_count: process.artifact_count ?? 0,
  });

  const loadProcesses = async () => {
    const fetchGen = ++primeFetchGenRef.current;
    const isStale = () => fetchGen !== primeFetchGenRef.current;

    try {
      setLoading(true);
      setPrimeFetchMeta(null);

      let processesData: ProcessInfo[] = [];

      // Company processes when tree/company context is present
      if (artifactTreeId || companyId || modelType === 'company') {
        // Fetch company processes filtered by artifact tree or company
        try {
          const params = new URLSearchParams();
          if (artifactTreeId) {
            params.append('artifact_tree_id', artifactTreeId);
          }
          if (companyId) {
            params.append('company_id', companyId);
          }

          const url = `/processes/${params.toString() ? `?${params.toString()}` : ''}`;
          const processesResponse = await apiClient.get(url);
          processesData = normalizeProcessList(processesResponse.data);
        } catch (error: any) {
          console.error('Error loading company processes:', error);
          // Fallback: try to get processes from artifacts in the tree
          if (artifactTreeId) {
            try {
              const artifactsResponse = await apiClient.get(`/artifacts?tree_id=${artifactTreeId}`);
              const artifacts = Array.isArray(artifactsResponse.data)
                ? artifactsResponse.data
                : (artifactsResponse.data?.artifacts || []);

              const allProcesses: any[] = [];
              for (const artifact of artifacts.slice(0, 50)) {
                try {
                  const processesResponse = await apiClient.get(`/artifacts/${artifact.id}/processes`);
                  const artifactProcesses = normalizeProcessList(processesResponse.data);
                  allProcesses.push(
                    ...artifactProcesses.map((p: any) => ({
                      ...p,
                      owner_artifact_name: artifact.name,
                      owner_artifact_id: artifact.id,
                    }))
                  );
                } catch (err) {
                  // Skip artifacts with no processes
                }
              }
              processesData = allProcesses;
            } catch (fallbackError) {
              console.error('Fallback also failed:', fallbackError);
            }
          }
        }
      } else if (isPrimeContext && selectedArtifactId) {
        // Prime artifact filter — never hit company /artifacts/{id}/processes (returns [] for prime UUIDs).
        try {
          const processesResponse = await apiClient.get(
            `/prime-artifacts/${selectedArtifactId}/processes`
          );
          processesData = normalizeProcessList(processesResponse.data).map((p: any) =>
            mapPrimeProcessRow(p, { name: 'This Artifact', id: selectedArtifactId })
          );
        } catch (primeError) {
          console.error('Prime artifact processes failed:', primeError);
        }
      } else if (isPrimeContext) {
        // Progressive pages of 100 until exhausted — keep UI responsive, update banner each page.
        const PAGE_SIZE = 100;
        const accumulated: ProcessWithArtifacts[] = [];
        setProcesses([]);
        setPrimeFetchMeta({
          loaded: 0,
          pageSize: PAGE_SIZE,
          complete: false,
          typeFilter,
        });

        try {
          let offset = 0;
          for (;;) {
            if (isStale()) return;
            const params = new URLSearchParams();
            params.set('limit', String(PAGE_SIZE));
            params.set('offset', String(offset));
            if (typeFilter !== 'all') {
              params.set('process_type', typeFilter);
            }
            const processesResponse = await apiClient.get(`/prime-processes?${params.toString()}`);
            if (isStale()) return;
            const page = normalizeProcessList(processesResponse.data).map((p: any) =>
              mapPrimeProcessRow(p)
            );
            accumulated.push(...page.map(toOverviewRow));
            setProcesses([...accumulated]);
            setPrimeFetchMeta({
              loaded: accumulated.length,
              pageSize: PAGE_SIZE,
              complete: page.length < PAGE_SIZE,
              typeFilter,
            });
            // First page ready — stop blocking the whole card with Spin.
            if (offset === 0) {
              setLoading(false);
            }
            if (page.length < PAGE_SIZE) {
              break;
            }
            offset += PAGE_SIZE;
          }
        } catch (error) {
          if (!isStale()) {
            console.error('Error loading prime processes:', error);
            message.error('Failed while loading prime processes; showing what was fetched so far.');
            setPrimeFetchMeta((prev) =>
              prev
                ? { ...prev, complete: true }
                : {
                    loaded: accumulated.length,
                    pageSize: PAGE_SIZE,
                    complete: true,
                    typeFilter,
                  }
            );
          }
        }
        if (isStale()) return;
        setLoading(false);
        return;
      }

      if (isStale()) return;

      const processesWithArtifacts = await Promise.all(
            processesData.map(async (process) => {
              try {
                let artifactsResponse;
                if (isPrimeContext) {
                  try {
                    artifactsResponse = await apiClient.get(`/prime-processes/${process.id}/artifacts`);
                  } catch (error) {
                    artifactsResponse = await apiClient.get(`/processes/${process.id}/artifacts`);
                  }
                } else {
                  try {
                    artifactsResponse = await apiClient.get(`/processes/${process.id}/artifacts`);
                  } catch (error) {
                    artifactsResponse = await apiClient.get(`/prime-processes/${process.id}/artifacts`);
                  }
                }

                const artifacts = Array.isArray(artifactsResponse.data)
                  ? artifactsResponse.data
                  : (artifactsResponse.data?.artifacts || []);

                return {
                  ...process,
                  artifacts: artifacts,
                };
              } catch (error) {
                return {
                  ...process,
                  artifacts: [],
                };
              }
            })
          );

      if (isStale()) return;
      setProcesses(processesWithArtifacts);
    } catch (error: any) {
      if (!isStale()) {
        message.error(`Failed to load processes: ${error.message}`);
        console.error('Error loading processes:', error);
      }
    } finally {
      if (!isStale()) {
        setLoading(false);
      }
    }
  };

  const filterProcesses = () => {
    let filtered = [...processes];

    // Search filter (type filter is server-side for prime overview)
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(term) ||
          (p.description && p.description.toLowerCase().includes(term)) ||
          p.artifacts.some((a) => a.artifact_name.toLowerCase().includes(term))
      );
    }

    // Type filter only when not already applied by the prime overview query
    if (typeFilter !== 'all' && !(isPrimeContext && !selectedArtifactId)) {
      filtered = filtered.filter((p) => p.process_type === typeFilter);
    }

    // Completion filter
    if (completionFilter !== 'all') {
      const isComplete = completionFilter === 'complete';
      filtered = filtered.filter((p) => p.is_complete === isComplete);
    }

    setFilteredProcesses(filtered);
  };

  const columns: ColumnsType<ProcessWithArtifacts> = [
    {
      title: 'Process Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <strong>{text}</strong>
          {record.description && (
            <span style={{ fontSize: '12px', color: '#666' }}>{record.description}</span>
          )}
        </Space>
      ),
      sorter: (a, b) => a.name.localeCompare(b.name)
    },
    {
      title: 'Type',
      dataIndex: 'process_type',
      key: 'process_type',
      render: (type) => (
        <Tag color={getProcessTypeColor(type)}>{type}</Tag>
      ),
      filters: [
        { text: ProcessType.DEFAULT, value: ProcessType.DEFAULT },
        { text: ProcessType.DEMAND, value: ProcessType.DEMAND },
        { text: ProcessType.SUPPLY, value: ProcessType.SUPPLY },
        { text: ProcessType.DELIVERY, value: ProcessType.DELIVERY },
        { text: ProcessType.FINANCE, value: ProcessType.FINANCE },
        { text: ProcessType.HR, value: ProcessType.HR },
        { text: ProcessType.IT, value: ProcessType.IT },
        { text: ProcessType.STRATEGY, value: ProcessType.STRATEGY },
        { text: ProcessType.IMPROVEMENT, value: ProcessType.IMPROVEMENT },
        { text: ProcessType.QUALITY, value: ProcessType.QUALITY },
        { text: ProcessType.COMPLIANCE, value: ProcessType.COMPLIANCE },
        { text: ProcessType.RESEARCH, value: ProcessType.RESEARCH },
        { text: ProcessType.MARKETFIT, value: ProcessType.MARKETFIT },
        { text: ProcessType.FITFINDER, value: ProcessType.FITFINDER }
      ],
      onFilter: (value, record) => record.process_type === value
    },
    {
      title: 'Artifacts',
      key: 'artifacts',
      render: (_, record) => (
        <Space size={[0, 4]} wrap>
          {record.artifacts.map((artifact, idx) => (
            <Tooltip key={idx} title={`Role: ${artifact.role}`}>
              <Tag color={getRoleColor(artifact.role)} style={{ margin: '2px' }}>
                {artifact.artifact_name}
              </Tag>
            </Tooltip>
          ))}
          {record.artifacts.length === 0 && (
            <Tag color={typeof record.artifact_count === 'number' ? 'default' : 'red'}>
              {typeof record.artifact_count === 'number'
                ? record.artifact_count > 0
                  ? `${record.artifact_count} linked`
                  : 'Overview (roles not loaded)'
                : 'No artifacts'}
            </Tag>
          )}
        </Space>
      )
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        if (record.artifacts.length === 0 && typeof record.artifact_count === 'number') {
          return (
            <Tag color="default" title="Role completeness needs per-process artifact load">
              Overview
            </Tag>
          );
        }
        const hasProcessItem = record.artifacts.some(a => 
          a.role === 'PROCESSITEM' || a.role === 'PROCESSITEM_PERSISTENT'
        );
        return hasProcessItem ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            Complete
          </Tag>
        ) : (
          <Tag icon={<WarningOutlined />} color="warning">
            Missing PROCESSITEM
          </Tag>
        );
      },
      filters: [
        { text: 'Complete', value: 'complete' },
        { text: 'Incomplete', value: 'incomplete' }
      ],
      onFilter: (value, record) => {
        const hasProcessItem = record.artifacts.some(a => 
          a.role === 'PROCESSITEM' || a.role === 'PROCESSITEM_PERSISTENT'
        );
        return value === 'complete' ? hasProcessItem : !hasProcessItem;
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="View/Edit">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => onProcessClick && onProcessClick(record.id)}
            />
          </Tooltip>
          <Tooltip title="View Flow">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => message.info('Flow view coming in Phase 4')}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  const processTypeGroups = filteredProcesses.reduce((acc, process) => {
    const type = process.process_type || 'UNKNOWN';
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(process);
    return acc;
  }, {} as Record<string, ProcessWithArtifacts[]>);

  return (
    <div className={styles.processCentricView}>
      <Card
        title={
          <Space>
            <span>Process-Centric View</span>
            {selectedArtifactId && (
              <Tag color="blue">Filtered by selected artifact</Tag>
            )}
          </Space>
        }
        extra={
          <Space>
            <Search
              placeholder="Search processes or artifacts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: 250 }}
              prefix={<SearchOutlined />}
            />
            <Select
              value={viewMode}
              onChange={setViewMode}
              style={{ width: 120 }}
            >
              <Option value="list">List View</Option>
              <Option value="grouped">Grouped View</Option>
            </Select>
            <Button
              icon={<PlusOutlined />}
              type="primary"
              onClick={() => message.info('Process creation coming soon')}
            >
              New Process
            </Button>
          </Space>
        }
      >
        <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
          {primeFetchMeta ? (
            <Tag color={primeFetchMeta.complete ? 'green' : 'processing'}>
              {primeFetchMeta.complete
                ? `Loaded ${primeFetchMeta.loaded} processes`
                : `Loading… ${primeFetchMeta.loaded} so far (+${primeFetchMeta.pageSize} per request)`}
              {primeFetchMeta.typeFilter !== 'all' ? ` · type ${primeFetchMeta.typeFilter}` : ''}
              {!primeFetchMeta.complete ? '. Table updates as pages arrive.' : '.'}
            </Tag>
          ) : null}
          <Space>
            <Select
              value={typeFilter}
              onChange={setTypeFilter}
              style={{ width: 200 }}
              placeholder="Filter by type"
            >
              <Option value="all">All Types</Option>
              <Option value={ProcessType.DEFAULT}>{ProcessType.DEFAULT}</Option>
              <Option value={ProcessType.DEMAND}>{ProcessType.DEMAND}</Option>
              <Option value={ProcessType.SUPPLY}>{ProcessType.SUPPLY}</Option>
              <Option value={ProcessType.DELIVERY}>{ProcessType.DELIVERY}</Option>
              <Option value={ProcessType.FINANCE}>{ProcessType.FINANCE}</Option>
              <Option value={ProcessType.HR}>{ProcessType.HR}</Option>
              <Option value={ProcessType.IT}>{ProcessType.IT}</Option>
              <Option value={ProcessType.STRATEGY}>{ProcessType.STRATEGY}</Option>
              <Option value={ProcessType.IMPROVEMENT}>{ProcessType.IMPROVEMENT}</Option>
              <Option value={ProcessType.QUALITY}>{ProcessType.QUALITY}</Option>
              <Option value={ProcessType.COMPLIANCE}>{ProcessType.COMPLIANCE}</Option>
              <Option value={ProcessType.RESEARCH}>{ProcessType.RESEARCH}</Option>
              <Option value={ProcessType.MARKETFIT}>{ProcessType.MARKETFIT}</Option>
              <Option value={ProcessType.FITFINDER}>{ProcessType.FITFINDER}</Option>
              <Option value="OPERATIONAL">OPERATIONAL</Option>
              <Option value="CREATION">CREATION</Option>
              <Option value="DEACTIVATION">DEACTIVATION</Option>
              <Option value="CLOSURE">CLOSURE</Option>
              <Option value="ACTIVATION">ACTIVATION</Option>
              <Option value="PROGRESSION">PROGRESSION</Option>
            </Select>
            <Select
              value={completionFilter}
              onChange={setCompletionFilter}
              style={{ width: 200 }}
              placeholder="Filter by status"
            >
              <Option value="all">All Status</Option>
              <Option value="complete">Complete</Option>
              <Option value="incomplete">Incomplete</Option>
            </Select>
            <Badge count={filteredProcesses.length} showZero>
              <Button icon={<FilterOutlined />}>Filtered Processes</Button>
            </Badge>
          </Space>
        </Space>

        <Spin spinning={loading && processes.length === 0}>
          {viewMode === 'list' ? (
            <Table
              dataSource={filteredProcesses}
              columns={columns}
              rowKey="id"
              pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
              size="small"
            />
          ) : (
            <Collapse
              defaultActiveKey={[]}
              items={Object.entries(processTypeGroups).map(([processType, typeProcesses]) => ({
                key: processType,
                label: (
                  <Space>
                    <Tag color={getProcessTypeColor(processType)}>{processType}</Tag>
                    <Badge count={typeProcesses.length} />
                  </Space>
                ),
                children: (
                  <Table
                    dataSource={typeProcesses}
                    columns={columns}
                    rowKey="id"
                    pagination={{ pageSize: 20 }}
                    size="small"
                  />
                )
              }))}
            />
          )}
        </Spin>
      </Card>
    </div>
  );
};

